import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useToast } from '../contexts/ToastContext';

interface TeamAccessListProps { shopId: string | null }

type AccessUser = {
  auth_user_id: string;
  email: string | null;
  name: string | null;
  status: string;
  invited_at: string | null;
  joined_at: string | null;
};

const TeamAccessList: React.FC<TeamAccessListProps> = ({ shopId }) => {
  const toast = useToast();
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("shop-access-list", { body: { shop_id: shopId } });
      if (error) throw error;
      const list = (data as any)?.users as AccessUser[];
      setUsers(Array.isArray(list) ? list : []);
    } catch (e: any) {
      const msg = e?.message || "Failed to load team access";
      if (String(msg).toLowerCase().includes("forbidden")) {
        toast.error("You do not have access to this shop.");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [shopId]);

  const fmt = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-base font-medium text-orange-400 ">Team Access</h4>
        <button onClick={load} disabled={loading || !shopId} className="px-3 py-1.5 border border-white/15  text-white  text-xs hover:bg-white/[0.08] disabled:opacity-50">Refresh</button>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[560px]">
          <div className="grid grid-cols-5 gap-2 py-2 text-xs  text-white/35">
            <div className="col-span-2 text-left">User</div>
            <div className="col-span-1 text-left">Status</div>
            <div className="col-span-1 text-left">Invited</div>
            <div className="col-span-1 text-left">Joined</div>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-white/[0.04]">
            {(!users || users.length === 0) && !loading ? (
              <div className="py-4 text-center text-white/35  text-sm">No team members</div>
            ) : (
              users.map((u) => {
                const label = u.email || "—";
                return (
                  <div key={u.auth_user_id} className="grid grid-cols-5 gap-2 items-center py-2">
                    <div className="col-span-2 text-left">
                      <div className="text-white  text-sm truncate">{label}</div>
                      <div className="text-white/35  text-xs truncate">{u.name || ""}</div>
                    </div>
                    <div className="col-span-1 text-left text-sm ">{u.status}</div>
                    <div className="col-span-1 text-left text-xs ">{fmt(u.invited_at)}</div>
                    <div className="col-span-1 text-left text-xs ">{fmt(u.joined_at)}</div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamAccessList;
